import React, { createContext, useState, useContext, useEffect } from "react";
import StorageService, {
  type ScanRecord,
  type Operator,
  type CameraConfig,
} from "../services/StorageService";

interface AppContextType {
  // User state
  operatorName: string;
  setOperatorName: (name: string) => void;

  // Scanning history
  scanHistory: Record<string, ScanRecord[]>;
  addScanRecord: (
    record: Omit<
      ScanRecord,
      "timestamp" | "operatorName" | "slotId" | "sessionId"
    >,
    slotId: number,
    sessionId?: string
  ) => void;

  // Operators tracking
  operators: Operator[];

  // Auth
  isManagerAuthenticated: boolean;
  setIsManagerAuthenticated: (auth: boolean) => void;

  // Camera configuration
  cameraConfig: CameraConfig;
  setCameraConfig: (config: CameraConfig) => void;

  // NEW: Current shift tracking
  currentShift: any | null;
  setCurrentShift: (shift: any | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Load initial state from storage
  const [operatorName, setOperatorNameState] = useState<string>(
    () => StorageService.getAppState().currentOperator || ""
  );
  const [scanHistory, setScanHistory] = useState<Record<string, ScanRecord[]>>(
    () => StorageService.getScanHistory()
  );
  const [operators, setOperators] = useState<Operator[]>(() =>
    StorageService.getOperators()
  );
  const [isManagerAuthenticated, setIsManagerAuthenticatedState] =
    useState<boolean>(() => StorageService.getManagerData().isAuthenticated);
  const [cameraConfig, setCameraConfigState] = useState<CameraConfig>(() =>
    StorageService.getCameraConfig()
  );
  // NEW: Add current shift state
  const [currentShift, setCurrentShift] = useState<any | null>(null);

  // Handle operator name changes
  const setOperatorName = (name: string) => {
    if (name) {
      // If there was a previous operator, log them out
      if (operatorName) {
        StorageService.logoutOperator(operatorName);
      }

      // Log in the new operator
      StorageService.addOperator(name);
      setOperatorNameState(name);
      StorageService.setCurrentOperator(name);

      // Refresh operators list
      setOperators(StorageService.getOperators());
    }
  };

  // Handle manager authentication changes
  const setIsManagerAuthenticated = (auth: boolean) => {
    setIsManagerAuthenticatedState(auth);
    StorageService.setManagerAuthenticated(auth);
  };

  // Handle camera configuration changes
  const setCameraConfig = (config: CameraConfig) => {
    setCameraConfigState(config);
    StorageService.setCameraConfig(config);
  };

  // Add scan record with current operator, slotId, and sessionId
  const addScanRecord = (
    record: Omit<
      ScanRecord,
      "timestamp" | "operatorName" | "slotId" | "sessionId"
    >,
    slotId: number,
    sessionId?: string
  ) => {
    const fullRecord = {
      ...record,
      operatorName,
    };

    StorageService.addScanRecord(fullRecord, slotId, sessionId);
    setScanHistory(StorageService.getScanHistory());
  };

  // Ensure proper logout on component unmount or page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Log out the current operator if there is one
      if (operatorName) {
        StorageService.logoutOperator(operatorName);
        // Force update localStorage synchronously before page unload
        try {
          localStorage.setItem("_temp", "_temp");
          localStorage.removeItem("_temp");
        } catch (e) {
          console.log("Error during beforeunload cleanup:", e);
          // Ignore errors
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Also handle component unmount
      if (operatorName) {
        StorageService.logoutOperator(operatorName);
      }
    };
  }, [operatorName]);

  const value = {
    operatorName,
    setOperatorName,
    scanHistory,
    addScanRecord,
    operators,
    isManagerAuthenticated,
    setIsManagerAuthenticated,
    cameraConfig,
    setCameraConfig,
    currentShift,
    setCurrentShift,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
